import React from 'react';
import { Form } from 'react-final-form';
import { isEmail, isMobilePhone } from 'utils/validators';
import FieldText from 'containers/profile/FieldText';
import FieldZip from 'containers/profile/FieldZip';
import { styled } from '@mui/material/styles';
import Rest from 'services/rest';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'recoil/atoms';
import { isUsZipcode } from '../../shared/utils/validators';
import { StyledButtonMui5 } from 'components/atoms/StyledButton';
import { MobilePhoneMask } from '../../containers/profile/MobilePhoneMask';
import Box from '@mui/material/Box';
import { useScreenSize } from '../../hooks/useScreenSize';

const rest = new Rest();

const FormWrapper = styled('form', {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{
  isCompact?: boolean;
}>(({ theme, isCompact }) => ({
  paddingTop: theme.spacing(1),
  ...(isCompact ? { paddingBottom: theme.spacing(3) } : {}),
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(3),
  },
}));

type FormData = {
  makeModelTrim: string;
  budget: string;
  additionalInfo: string;
  zipcode: string;
  name: string;
  email: string;
  phoneNumber: string;
};

type Props = {
  onSubmit: () => void;
  submitBtnAlign?: 'center' | 'right';
  fullWidth?: boolean;
};

function MakeMeAnOfferForm({
  onSubmit,
  submitBtnAlign = 'right',
  fullWidth = false,
}: Props): JSX.Element {
  const user = useRecoilValue(userAtom);
  const { isExtraSmallScreen: isCompact } = useScreenSize();
  fullWidth = fullWidth ?? isCompact;

  async function submit(data: FormData) {
    Object.keys(data).forEach((key) => (data[key] = data[key]?.trim?.()));
    await rest.sendMakeMeAnOfferEmail(data);
    onSubmit();
  }

  function validate(values: FormData) {
    const errors: Partial<FormData> = {};

    if (!values.makeModelTrim) {
      errors.makeModelTrim = 'Required';
    }
    if (!values.budget) {
      errors.budget = 'Invalid budget';
    }
    if (!values.name) {
      errors.name = 'Required';
    }
    if (!isEmail(values.email)) {
      errors.email = 'Invalid email address';
    }
    if (!values.zipcode) {
      errors.zipcode = 'Zip code required';
    } else if (!isUsZipcode(values.zipcode)) {
      errors.zipcode = 'Invalid zip code';
    }
    if (!isMobilePhone(values.phoneNumber)) {
      errors.phoneNumber = 'Invalid phone number';
    }

    return errors;
  }

  const initialValues: FormData = {
    makeModelTrim: '',
    budget: '',
    additionalInfo: '',
    zipcode: user ? user.zipcode : '',
    name: user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : '',
    email: user ? user.email : '',
    phoneNumber: user ? user.mobile_phone : '',
  };

  return (
    <Form
      onSubmit={submit}
      initialValues={initialValues}
      validate={validate}
      mutators={{
        setZipcode: ([zipcode], state, utils) => {
          utils.changeValue(state, 'zipcode', () => zipcode);
        },
      }}
      render={({ handleSubmit, submitting, form }) => (
        <FormWrapper
          autoComplete='off'
          onSubmit={handleSubmit}
          isCompact={isCompact}
          noValidate
        >
          <FieldText
            label='Car'
            name='makeModelTrim'
            required
            placeholder='make/model/trim'
          />
          <FieldText
            label='Monthly Budget'
            name='budget'
            placeholder='Preferred monthly payment'
            required
          />
          <FieldText
            multiline
            label='Additional Info'
            name='additionalInfo'
            sx={{ '& .MuiInputBase-root': { paddingY: 2 } }}
            placeholder='reference link, vehicle features etc.'
          />
          <FieldZip setZipcode={form.mutators.setZipcode} required />
          <FieldText label='Email' name='email' required />
          <FieldText label='Name' name='name' required />
          <FieldText
            InputProps={{
              inputComponent: MobilePhoneMask,
            }}
            name='phoneNumber'
            label='Cell Number'
            required
          />
          <Box
            display='flex'
            justifyContent={submitBtnAlign === 'center' ? 'center' : 'flex-end'}
          >
            <StyledButtonMui5
              sx={{ '&.MuiButtonBase-root': { borderRadius: '5px' } }}
              variant='contained'
              color='primary'
              type='submit'
              disabled={submitting}
              onClick={handleSubmit}
              fullWidth={fullWidth}
            >
              Submit
            </StyledButtonMui5>
          </Box>
        </FormWrapper>
      )}
    />
  );
}

export default MakeMeAnOfferForm;
